/**
 * PageState — empty / loading / error / denied state card.
 *
 * Mirrors the Figma PageState variant set. Replaces ~20 hand-rolled
 * "state card" copies (MembersEmpty, MembersError, MembersDenied,
 * RolesEmpty, RolesError, FilterEmpty, account StateViews, mcp
 * PageStates, settings PageStates, onboarding PageStates …).
 *
 *   <PageState state="empty" icon={IconUsers} headline="No teammates yet"
 *     body="..." cta={<Button>Invite</Button>} />
 *
 *   <PageState state="error" headline="Couldn’t load this page"
 *     body="..." cta={<Button variant="secondary">Retry</Button>} />
 */
import { type ComponentType, type ReactElement, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { textsRevealContainer, textsRevealLine } from '@shared/motion';
import {
  IconAlert,
  IconErrorCircle,
  IconLock,
  IconPlay,
  IconRefresh,
  type IconProps,
} from '@shared/icons';
import { MatrixLoader } from '../MatrixLoader';
import { EmptyPreview, type PreviewShape } from './EmptyPreview';

export type PageStateKind = 'empty' | 'loading' | 'error' | 'denied';

export type PageStateProps = {
  state: PageStateKind;
  /** Override the default icon for this state. */
  icon?: ComponentType<IconProps>;
  headline: string;
  body?: ReactNode;
  /** Trailing action element (typically a <Button>). */
  cta?: ReactNode;
  /**
   * Product-Preview treatment for a first-run empty: a dimmed skeleton mock of
   * this page's own populated layout, shown above the headline/body/CTA in
   * place of the icon chip. Only honoured when `state="empty"`. Pick the shape
   * that matches the real content (`table` for a log, `card-grid` for a
   * registry, `graph` for a topology, …).
   */
  preview?: PreviewShape;
  /** Show a chip over the preview mock (onboarding pages). Defaults to a
   * "Watch demo" play chip; override with `demoLabel` / `demoIcon` / `demoHref`. */
  demo?: boolean;
  /** Chip label. Default "Watch demo". */
  demoLabel?: string;
  /** Chip leading icon. Default IconPlay. */
  demoIcon?: ComponentType<IconProps>;
  /** When set, the chip is a link (opens in a new tab) — e.g. a docs page. */
  demoHref?: string;
  /**
   * Drop the card chrome (border, surface background, rounded box, default
   * padding) and render the mark + text on the bare page. Useful for a
   * full-page loading state that should read as the loader alone rather than a
   * card floating in the middle of the screen.
   */
  bare?: boolean;
  /**
   * Stretch the card to fill its parent (full width + height) and center the
   * content vertically as well as horizontally. Turns the compact,
   * content-hugging box into a large framed area that owns the empty space.
   * Give the parent a height (e.g. a `min-h-[…]` flex wrapper) for it to fill.
   */
  fill?: boolean;
  className?: string;
};

const STATE_CHIP: Record<PageStateKind, string> = {
  empty: 'bg-aq-accent-soft text-aq-accent',
  loading: 'bg-aq-zebra text-aq-ink-muted',
  error: 'bg-aq-bad-soft text-aq-bad',
  denied: 'bg-aq-warn-soft text-aq-warn',
};

const DEFAULT_ICON: Record<PageStateKind, ComponentType<IconProps>> = {
  empty: IconAlert,
  loading: IconRefresh,
  error: IconErrorCircle,
  denied: IconLock,
};

export function PageState({
  state,
  icon,
  headline,
  body,
  cta,
  preview,
  demo,
  demoLabel = 'Watch demo',
  demoIcon: DemoIcon = IconPlay,
  demoHref,
  bare = false,
  fill = false,
  className,
}: PageStateProps): ReactElement {
  const Icon = icon ?? DEFAULT_ICON[state];
  const reduce = useReducedMotion();
  // The mark, headline, body, and CTA rise in with a gentle stagger. The
  // loading state shows the brand MatrixLoader instead of a spinning icon
  // chip; reduced-motion users get the content with no transform (MatrixLoader
  // itself resolves reduced-motion via CSS, so it stays visible but calm).
  const line = reduce ? undefined : textsRevealLine;
  const isLoading = state === 'loading';
  // Product-Preview treatment: only for a first-run empty that opted in.
  const showPreview = state === 'empty' && !!preview;
  // `bare` drops the card chrome so the content reads on the page directly.
  // `fill` stretches the card to own its parent's space and centers content
  // vertically (a large framed empty area) instead of hugging its content.
  const chrome = [
    bare
      ? 'flex flex-col items-center text-center'
      : 'border-aq-border bg-aq-surface flex flex-col items-center rounded-xl border px-6 py-12 text-center',
    // `min-h-full` + `flex-1` makes it fill whether the parent is a plain block
    // (min-h-full resolves against parent height) or a flex column (flex-1 grows).
    fill ? 'min-h-full w-full flex-1 justify-center' : '',
  ].join(' ');

  return (
    <motion.div
      role="status"
      aria-live={state === 'loading' ? 'polite' : 'off'}
      className={[chrome, className ?? ''].join(' ')}
      variants={reduce ? undefined : textsRevealContainer}
      initial={reduce ? false : 'hidden'}
      animate="visible"
    >
      {showPreview ? (
        // Dimmed skeleton mock of this page's real layout, masked at the bottom
        // so it reads as "behind" the copy. Optional "Watch demo" play chip.
        <motion.div variants={line} className="relative mb-6 w-full max-w-lg">
          <div aria-hidden="true" className="pointer-events-none opacity-60">
            <EmptyPreview shape={preview} />
          </div>
          <div className="from-aq-surface pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t to-transparent" />
          {demo ? (
            // Floating pill (leading icon badge + label), centered over the mock.
            // A link when `demoHref` is set (e.g. docs), otherwise a plain button.
            demoHref ? (
              <a
                href={demoHref}
                target="_blank"
                rel="noreferrer"
                className="border-aq-border bg-aq-surface text-aq-ink hover:border-aq-accent text-aq-sm absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border py-2 pr-4 pl-3 font-semibold no-underline shadow-md transition-colors"
              >
                <span className="bg-aq-accent-soft text-aq-accent flex h-6 w-6 items-center justify-center rounded-full">
                  <DemoIcon size={13} stroke={2} />
                </span>
                {demoLabel}
              </a>
            ) : (
              <button
                type="button"
                className="border-aq-border bg-aq-surface text-aq-ink hover:border-aq-accent text-aq-sm absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border py-2 pr-4 pl-3 font-semibold shadow-md transition-colors"
              >
                <span className="bg-aq-accent-soft text-aq-accent flex h-6 w-6 items-center justify-center rounded-full">
                  <DemoIcon size={13} stroke={2} />
                </span>
                {demoLabel}
              </button>
            )
          ) : null}
        </motion.div>
      ) : isLoading ? (
        <motion.div
          variants={line}
          className="text-aq-accent flex h-12 items-center justify-center"
        >
          <MatrixLoader pattern="wave" size="md" />
        </motion.div>
      ) : (
        <motion.div
          variants={line}
          className={`flex h-12 w-12 items-center justify-center rounded-lg ${STATE_CHIP[state]}`}
        >
          <Icon size={22} stroke={1.6} />
        </motion.div>
      )}
      <motion.div
        variants={line}
        className={[
          'text-aq-ink mt-3 font-semibold',
          // Loading labels sit one notch smaller than empty/error/denied
          // headlines — the loader is the focus, the label is a caption.
          isLoading ? 'text-aq-sm' : 'text-aq-md',
        ].join(' ')}
      >
        {headline}
      </motion.div>
      {body ? (
        <motion.div
          variants={line}
          className="text-aq-ink-muted text-aq-sm mt-1 max-w-md leading-snug"
        >
          {body}
        </motion.div>
      ) : null}
      {cta ? (
        <motion.div variants={line} className="mt-4">
          {cta}
        </motion.div>
      ) : null}
    </motion.div>
  );
}
